import {ParentView} from "../parentView";
import {EventTypes} from "../../helpers/eventtypes";
import {ToastType} from "../../helpers/toasttype";

class LoginView extends ParentView {
    constructor(formId: string, btnSaveId: string) {
        super(formId, btnSaveId);
    }

    private AddPasswordToggleEvents(): void {
        const toggles = document.querySelectorAll<HTMLButtonElement>("[data-password-toggle]");

        toggles.forEach(toggle => {
            toggle.addEventListener("click", () => {
                const targetId = toggle.getAttribute("data-target-id");
                if (!targetId) {
                    return;
                }

                const targetInput = document.getElementById(targetId) as HTMLInputElement | null;
                if (!targetInput) {
                    return;
                }

                const isMasked = targetInput.type === "password";
                targetInput.type = isMasked ? "text" : "password";

                // Toggle the icon
                const iconElement = toggle.querySelector("i");
                if (iconElement) {
                    iconElement.classList.toggle("fa-eye", !isMasked);
                    iconElement.classList.toggle("fa-eye-slash", isMasked);
                }

                const textElement = toggle.querySelector("span");
                if (textElement) {
                    textElement.textContent = isMasked ? "Hide" : "Show";
                }

                toggle.setAttribute("aria-label", isMasked ? "Hide password" : "Show password");
                toggle.setAttribute("aria-pressed", isMasked ? "true" : "false");
            });
        });
    }

    private bindPasskeyLoginButton(): void {
        const btnPasskey = document.getElementById('btnPasskeyLogin');
        if (btnPasskey) {
            btnPasskey.addEventListener('click', () => this.authenticateWithPasskey());
        }
    }

    private async authenticateWithPasskey(): Promise<void> {
        const statusEl = document.getElementById('passkeyStatus');
        const btnPasskey = document.getElementById('btnPasskeyLogin') as HTMLButtonElement;

        try {
            // Check WebAuthn support
            if (!window.PublicKeyCredential) {
                this.showPasskeyStatus('Your browser does not support passkeys.', 'danger');
                return;
            }

            // Disable button while processing
            if (btnPasskey) {
                btnPasskey.disabled = true;
            }

            this.showPasskeyStatus('Preparing passkey authentication...', 'info');

            // Get the email if entered (optional - for user-specific credentials)
            const emailInput = document.getElementById('Input_Email') as HTMLInputElement;
            const username = emailInput?.value?.trim() || null;
            const returnUrl = (document.getElementById('returnUrl') as HTMLInputElement)?.value || '/';

            // Get request options from server
            const optionsResponse = await fetch('/Passkey/RequestOptions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'RequestVerificationToken': this.getAntiForgeryToken()
                },
                body: JSON.stringify({ username: username })
            });

            if (!optionsResponse.ok) {
                const error = await optionsResponse.json();
                this.showPasskeyStatus(error.error || 'Failed to get authentication options.', 'danger');
                return;
            }

            const options = await optionsResponse.json();

            // Convert base64url strings to ArrayBuffers
            options.challenge = this.base64UrlToArrayBuffer(options.challenge);

            if (options.allowCredentials) {
                options.allowCredentials = options.allowCredentials.map((cred: any) => ({
                    ...cred,
                    id: this.base64UrlToArrayBuffer(cred.id)
                }));
            }

            this.showPasskeyStatus('Please follow the prompts from your device...', 'info');

            // Call WebAuthn API
            const credential = await navigator.credentials.get({ publicKey: options }) as PublicKeyCredential;

            if (!credential) {
                this.showPasskeyStatus('Passkey authentication was cancelled.', 'warning');
                return;
            }

            // Prepare credential for server
            const assertionResponse = credential.response as AuthenticatorAssertionResponse;
            const credentialJson = JSON.stringify({
                id: credential.id,
                rawId: this.arrayBufferToBase64Url(credential.rawId),
                type: credential.type,
                clientExtensionResults: credential.getClientExtensionResults ? credential.getClientExtensionResults() : {},
                authenticatorAttachment: credential.authenticatorAttachment ?? null,
                response: {
                    clientDataJSON: this.arrayBufferToBase64Url(assertionResponse.clientDataJSON),
                    authenticatorData: this.arrayBufferToBase64Url(assertionResponse.authenticatorData),
                    signature: this.arrayBufferToBase64Url(assertionResponse.signature),
                    userHandle: assertionResponse.userHandle ? this.arrayBufferToBase64Url(assertionResponse.userHandle) : null
                }
            });

            this.showPasskeyStatus('Verifying passkey...', 'info');

            // Authenticate with server
            const authResponse = await fetch('/Passkey/Authenticate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'RequestVerificationToken': this.getAntiForgeryToken()
                },
                    body: JSON.stringify({ credentialJson: credentialJson, returnUrl: returnUrl })
            });

            const authResult = await authResponse.json();

            if (!authResponse.ok) {
                this.showPasskeyStatus(authResult.error || 'Authentication failed.', 'danger');
                return;
            }

            if (authResult.requiresTwoFactor) {
                // Redirect to 2FA page
                this.showPasskeyStatus('Two-factor authentication required. Redirecting...', 'info');
                window.location.href = `/Loginwith2fa?ReturnUrl=${encodeURIComponent(returnUrl)}`;
                return;
            }

            this.showPasskeyStatus('Authentication successful! Redirecting...', 'success');
            this.utility.sayToast('Signed in successfully!', ToastType.Success);

            // Redirect to return URL or home
                window.location.href = authResult.redirectUrl || returnUrl;

        } catch (error: any) {
            console.error('Passkey authentication error:', error);
            if (error.name === 'NotAllowedError') {
                this.showPasskeyStatus('Passkey authentication was cancelled or not allowed.', 'warning');
            } else if (error.name === 'SecurityError') {
                this.showPasskeyStatus('Security error. Please ensure you are on a secure connection.', 'danger');
            } else {
                this.showPasskeyStatus(`Authentication failed: ${error.message}`, 'danger');
            }
        } finally {
            if (btnPasskey) {
                btnPasskey.disabled = false;
            }
        }
    }

    private showPasskeyStatus(message: string, type: 'info' | 'success' | 'warning' | 'danger'): void {
        const statusEl = document.getElementById('passkeyStatus');
        if (statusEl) {
            statusEl.className = `alert alert-${type} margin-top-2`;
            statusEl.textContent = message;
            statusEl.style.display = 'block';
        }
    }

    private getAntiForgeryToken(): string {
        const tokenInput = document.querySelector('input[name="__RequestVerificationToken"]') as HTMLInputElement;
        return tokenInput?.value || '';
    }

    private base64UrlToArrayBuffer(base64url: string): ArrayBuffer {
        const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
        const padding = '='.repeat((4 - base64.length % 4) % 4);
        const base64padded = base64 + padding;
        const binary = atob(base64padded);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes.buffer;
    }

    private arrayBufferToBase64Url(buffer: ArrayBuffer): string {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }

    private updateInputWrapperValidation(): void {
        // Find all input wrappers and update their validation state
        const wrappers = document.querySelectorAll<HTMLElement>('.input-wrapper');
        wrappers.forEach(wrapper => {
            const input = wrapper.querySelector('input');
            if (input) {
                if (input.classList.contains('input-validation-error')) {
                    wrapper.classList.add('input-validation-error', 'has-error');
                } else {
                    wrapper.classList.remove('input-validation-error', 'has-error');
                }
            }
        });
    }

    InitView(): void {
        let btnSave: HTMLButtonElement = <HTMLButtonElement>(document.getElementById(this.btnSaveId) as HTMLButtonElement);
        if (btnSave) {
            let obj = this;
            btnSave!.addEventListener("click", function (event) {
                if (obj.utility.ValidateForm(obj.formId)) {
                    // Clear validation error styling on success
                    obj.updateInputWrapperValidation();
                }
                else {
                    event.preventDefault();
                    // Update wrapper validation states to show full border
                    obj.updateInputWrapperValidation();
                    obj.utility.sayToast("Please correct Errors!", ToastType.Error);
                }
            });
        }
        else {
            this.utility.sayToast("btnSave not found", ToastType.Error);
        }

        this.AddPasswordToggleEvents();
        this.bindPasskeyLoginButton();
    }
}
export { LoginView }
/* add Listener */
document.addEventListener(EventTypes.DOMContentLoaded, () => {
    let obj: LoginView = new LoginView("login-form", "btnLogin");
    obj.InitView();
});
